import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { get } from 'lodash';
import {
  renderDateMY,
  renderTimeMY,
  getTransactionId,
  getReferenceNo,
  getPaymentType,
  getFormattedSlotId,
  getErrorCategoryMessage,
  getSanitizedErrorDetail,  
} from '@/utils/transaction'

const useStyles = makeStyles(theme => ({
  customDialog: {
    '& .MuiDialog-paperWidthSm': {
      // Always allow up to 50rem, but never wider than the screen
      maxWidth: '50rem',
      width: '100%', // baseline: take full available width
      margin: '1rem', // 1rem (=16px) gutter on all sides

      // On small-and-up screens, let width shrink to content (up to maxWidth)
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
        margin: 'auto',
      },

      // Same background & padding
      backgroundColor: '#f0f0f0',
      padding: '1rem',
    },
  },

  title: {
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: '#212121',
  },

  dialogContent: {
    padding: '1rem',
    backgroundColor: '#fff',
    fontSize: '1.125rem',
    minHeight: '12.5rem',
  },

  // Let the table fill the dialog’s inner width
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },

  // In narrow mode these will wrap or ellipsize; you can
  // reduce these rems if you need them slimmer on phone
  keyCell: {
    width: '12rem',
    fontSize: '1rem',
    color: '#616161',
  },
  valueCell: {
    width: '20rem', // 320px
    fontSize: '1rem',
    color: '#212121',
  },

  closeButton: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    color: theme.palette.grey[500],
    padding: '0.25rem',
  },
}));

// check the status
const checkStatus = (classes, v) => {
  const { status } = v;
  switch (status) {
    case -4:
      return (
        <TableCell align="left" style={{ color: '#FF6562' }} colSpan={1} className={classes.valueCell}>
          DispensingEr
        </TableCell>
      );
    case -3:
      return (
        <TableCell align="left" style={{ color: '#FF6562' }} colSpan={1} className={classes.valueCell}>
          Void
        </TableCell>
      );
    case -2:
      return (
        <TableCell align="left" style={{ color: '#FF6562' }} colSpan={1} className={classes.valueCell}>
          Dropped
        </TableCell>
      );
    case -1:
      return (
        <TableCell align="left" style={{ color: '#C0C0C0' }} colSpan={1} className={classes.valueCell}>
          Canceled
        </TableCell>
      );
    case 0:
      return (
        <TableCell align="left" style={{ color: '#FF6562' }} colSpan={1} className={classes.valueCell}>
          Failed
        </TableCell>
      );
    case 1:
      return (
        <TableCell align="left" style={{ color: '#00C07F' }} colSpan={1} className={classes.valueCell}>
          Successful
        </TableCell>
      );
  }
};

const getCashType = (cashTypeObject, transType) => {
  const receivedArray = ['0.1', '0.2', '0.5', '1.0', '5.0', '10.0'];
  const returnedArray = ['0.1', '0.2', '0.5'];

  var resultArray = [];

  if (cashTypeObject == null) {
    return;
  }

  switch (transType) {
    case 'received':
      for (var i = 0; i < receivedArray.length; i++) {
        const cashTypeValue = get(cashTypeObject, receivedArray[i]);

        if (cashTypeValue != null && cashTypeValue != 0) {
          resultArray.push(`${cashTypeValue}`);
        } else {
          resultArray.push(`0`);
        }
      }
      return resultArray;
    case 'returned':
      for (var i = 0; i < returnedArray.length; i++) {
        const cashTypeValue = get(cashTypeObject, returnedArray[i]);

        if (cashTypeValue != null && cashTypeValue != 0) {
          resultArray.push(`${cashTypeValue}`);
        } else {
          resultArray.push(`0`);
        }
      }
      return resultArray;
  }
};

const OpenCash = ({ classes, value }) => {
  const cashTypeArray = ['10 cent', '20 cent', '50 cent', '1 ringgit', '5 ringgit', '10 ringgit'];
  var totalCashReceived = get(value, 'cashInDetails.cashInTotal') ? get(value, 'cashInDetails.cashInTotal') : 0;
  var totalCashReturned = get(value, 'cashOutDetails.cashOutTotal') ? get(value, 'cashOutDetails.cashOutTotal') : 0;
  var balance = (totalCashReceived - totalCashReturned).toFixed(1);
  var totalCashTypeReceived = getCashType(get(value, 'cashInDetails.cashInTypeAndCount'), 'received');
  var totalCashTypeReturned = getCashType(get(value, 'cashOutDetails.cashOutTypeAndCount'), 'returned');

  return (
    <>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          <Typography variant="h5">Payment Details</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Payment Type
        </TableCell>
        <TableCell align="left" className={classes.valueCell}>
          Cash
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Coin Type Received:
        </TableCell>
      </TableRow>

      {totalCashTypeReceived ? (
        totalCashTypeReceived.map((eachType, index) => (
          <TableRow key={index}>
            <TableCell align="left" className={classes.valueCell}>
              {cashTypeArray[index]}
            </TableCell>
            <TableCell align="left" className={classes.valueCell}>
              =
            </TableCell>
            <TableCell align="left" className={classes.valueCell}>
              {eachType}
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan="3" align="right">
            N/A
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell align="left" className={classes.valueCell}></TableCell>
        <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
          Total:
        </TableCell>
        {totalCashTypeReceived ? (
          <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
            RM {totalCashReceived}
          </TableCell>
        ) : (
          <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
            N/A
          </TableCell>
        )}
      </TableRow>

      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Coin Type Returned:
        </TableCell>
      </TableRow>
      {totalCashTypeReturned ? (
        totalCashTypeReturned.map((eachType, index) => (
          <TableRow key={index}>
            <TableCell align="left" className={classes.valueCell}>
              {cashTypeArray[index]}
            </TableCell>
            <TableCell align="left" className={classes.valueCell}>
              =
            </TableCell>
            <TableCell align="left" className={classes.valueCell}>
              {eachType}
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell align="left" className={classes.valueCell}>
            N/A
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell align="left" className={classes.valueCell}></TableCell>
        <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
          Total:
        </TableCell>
        {totalCashTypeReturned ? (
          <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
            RM {totalCashReturned}
          </TableCell>
        ) : (
          <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
            N/A
          </TableCell>
        )}
      </TableRow>

      <TableRow>
        <TableCell align="left" className={classes.valueCell}></TableCell>
        <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
          Balance:
        </TableCell>
        {totalCashTypeReceived && totalCashTypeReturned ? (
          <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
            RM {balance}
          </TableCell>
        ) : (
          <TableCell align="left" style={{ fontWeight: 600 }} className={classes.valueCell}>
            N/A
          </TableCell>
        )}
      </TableRow>
    </>
  );
};

const OpenCashless = ({ classes, value }) => {
  return (
    <>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          <Typography variant="h5">Payment Details</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Payment Type:
        </TableCell>
        <TableCell align="left" className={classes.valueCell}>
          {getPaymentType(value)}
        </TableCell>
      </TableRow>
      {get(value, 'mode') === 'commercial' && (
        <>
          <TableRow>
            <TableCell align="left" className={classes.keyCell}>
              Transaction ID:
            </TableCell>
            <TableCell align="left" className={classes.valueCell}>
              {getTransactionId(value)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" className={classes.keyCell}>
              Ref.No:
            </TableCell>
            <TableCell align="left" className={classes.valueCell}>
              {getReferenceNo(value)}
            </TableCell>
          </TableRow>
        </>
      )}
      {get(value, 'mode') === 'mro' &&
        (get(value, 'payment.paymentType') === 'mro' || get(value, 'payment.paymentType') == null ? (
          <>
            <TableRow>
              <TableCell align="left" className={classes.keyCell}>
                Staff Name:
              </TableCell>
              <TableCell align="left" className={classes.valueCell}>
                {get(value, 'employee.fullName', '')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="left" className={classes.keyCell}>
                Staff Card:
              </TableCell>
              <TableCell align="left" className={classes.valueCell}>
                {get(value, 'scanCode', '')}
              </TableCell>
            </TableRow>
          </>
        ) : (
          <>
            <TableRow>
              <TableCell align="left" className={classes.keyCell}>
                Transaction ID:
              </TableCell>
              <TableCell align="left" className={classes.valueCell}>
                {getTransactionId(value)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="left" className={classes.keyCell}>
                Ref.No:
              </TableCell>
              <TableCell align="left" className={classes.valueCell}>
                {getReferenceNo(value)}
              </TableCell>
            </TableRow>
          </>
        ))}
    </>
  );
};

const OpenFailDetails = ({ classes, value }) => {
  const { category, message } = getErrorCategoryMessage(value);

  return (
    <>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          <Typography variant="h5">Error Details</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Category:
        </TableCell>
        <TableCell align="left" className={classes.valueCell}>
          {category}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={1} className={classes.keyCell}>
          Message:
        </TableCell>
        <TableCell align="left" colSpan={1} className={classes.valueCell}>
          {message}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={1} className={classes.keyCell}>
          Description:
        </TableCell>
        <TableCell align="left" colSpan={1} className={classes.valueCell}>
          {getSanitizedErrorDetail(value)}
        </TableCell>
      </TableRow>
    </>
  );
};

export default function CustomizedDialogs(record) {
  const value = record;
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const classes = useStyles();

  return (
    <div>
      <Button variant="text" color="primary" onClick={handleClickOpen}>
        &gt;
      </Button>
      <Dialog onClose={handleClose} open={open} className={classes.customDialog}>
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          <Typography className={classes.title}>ID: {get(value, 'id')}</Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Table className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell align="left" className={classes.keyCell} colSpan={1}>
                  <Typography variant="h5">Transaction Details</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={1} className={classes.keyCell}>
                  Machine UUID:{' '}
                </TableCell>
                <TableCell align="left" colSpan={1} className={classes.valueCell}>
                  {get(value, 'machine.machineUUID', '')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={1} className={classes.keyCell}>
                  Date:{' '}
                </TableCell>
                <TableCell align="left" colSpan={1} className={classes.valueCell}>
                  {renderDateMY(value)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={1} className={classes.keyCell}>
                  Time:{' '}
                </TableCell>
                <TableCell align="left" colSpan={1} className={classes.valueCell}>
                  {renderTimeMY(value)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={1} className={classes.keyCell}>
                  Product Name:{' '}
                </TableCell>
                <TableCell align="left" colSpan={1} className={classes.valueCell}>
                  {get(value, 'product.name', '')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={1} className={classes.keyCell}>
                  Product Slot:{' '}
                </TableCell>
                <TableCell align="left" colSpan={1} className={classes.valueCell}>
                  {getFormattedSlotId(value)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" className={classes.keyCell}>
                  Product Price:
                </TableCell>
                <TableCell align="left" className={classes.valueCell}>
                  {(() => {
                    const m = get(value, 'mode');
                    const a = get(value, 'amount');
                    const p = get(value, 'payment.paymentType');
                    return m === 'mro' && (p == null || p === 'mro') ? `${a} Credit${a !== 1 ? 's' : ''}` : `RM ${a}`;
                  })()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={1} className={classes.keyCell}>
                  Status:{' '}
                </TableCell>
                {checkStatus(classes, value)}
              </TableRow>
              {get(value, 'payment.cash') ? (
                <OpenCash classes={classes} value={value}/>
              ) : (
                <OpenCashless classes={classes} value={value} />
              )}
              {get(value, 'status') == 1 ? <></> : <OpenFailDetails classes={classes} value={value} />}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
